import { createApi } from '@reduxjs/toolkit/query/react';
import EventDTO, {
  BasicEventDTO,
  BasicEventsQueryParams,
  CreateEventAudienceDiscountDTO, EventAssistanceReportDTO, EventBalanceDTO, EventAudienceDiscountDTO, SellerEventQR,
  SellerReferralsReportDTO,
  EventSubscriptionDTO,
  CreateEventSubscriptionDTO,
  CategoryDTO,
  EventCategoryDTO,
  EventSummaryDTO,
  EventStatsDTO,
  AllEventsPendingBalanceDTO, PendingBalanceAtDTO,
  PlatformSalesChartDTO,
  PlatformChartsDTO,
  PlatformRangeChartsDTO,
  PlatformMonthlyChartsDTO,
  TopGrossingDTO,
  ProvinceDTO,
  CityDTO,
  PublicCategoryDTO,
  PublicProducerDTO,
} from '../../models/event';
import { Response } from '../../models/shared';
import { AgentDTO } from '../../models/agent';
import baseQueryWithReauth from './base';
import { dedupePublicCategoriesBySlug } from '../../utils/dedupePublicCategories';
import { toArgentinaReportBounds } from '../../utils/dates';
import {
  QuestionDTO,
  PollResponse,
  CreatePollQuestionDTO,
  UpdatePollQuestionDTO,
} from '../../models/poll';
import {
  AttendeeFieldDTO,
  CreateAttendeeFieldDTO,
  UpdateAttendeeFieldDTO,
} from '../../models/attendee-field';
import { DisplaySeatDTO, SeatAvailabilityDTO, SectorForSale } from '../../models/seat';
import UserDTO, { UserEvent } from '../../models/user';
import { CreateEventTransactionDTO, EventTransactionDTO } from '../../models/event-transaction';
// eslint-disable-next-line import/no-cycle
import { ordersApi } from './orders';
// eslint-disable-next-line import/no-cycle
import { businessesApi } from './business';
import { CreateWireTransferDTO, WireTransferDTO } from '../../models/wire-transfer';
import { EventSeatBlockReason, EventSeatDTO } from '../../models/event-seat';
import { referralsApi } from './referrals';
import { CreateShowtimeDTO, ShowtimeDTO } from '../../models/showtime';

export interface ItemExportRow {
  id: string;
  order_display_id: string;
  item_ticket_id: string;
  name: string;
  last_name: string;
  qr_code: string;
}

const eventsApi = createApi({
  reducerPath: 'eventsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['events', 'event_capacity', 'events_users', 'businesses_users', 'businesses', 'events_transactions', 'event_seats', 'event_seats_raw', 'seller_summary', 'orders', 'event_assistance_report', 'event_sellers_report', 'event_audience_discounts', 'showtimes', 'event_categories', 'public_taxonomy', 'poll_questions', 'attendee_fields'],
  endpoints: (build) => ({
    getEventById: build.query<EventDTO | null, { eventId: string, slug?: boolean }>({
      providesTags: (result, error, { eventId }) => [{ type: 'events', id: eventId }, { type: 'events' }],
      query: ({ eventId, slug }) => ({
        url: slug ? `/events/slug/${eventId}` : `/events/${eventId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventDTO>): (EventDTO | null) => response.data || null,
    }),
    getEventBySlug: build.query<EventDTO | null, { slug: string }>({
      providesTags: (result, error, { slug }) => [{ type: 'events', id: slug }, { type: 'events' }],
      query: ({ slug }) => ({
        url: `/events/slug/${slug}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventDTO>): (EventDTO | null) => response.data || null,
    }),
    getAllEvents: build.query<EventDTO[] | null, { query?: Partial<EventDTO> }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ query }) => ({
        url: '/events/all/available',
        params: query,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventDTO[]>): (EventDTO[] | null) => response.data || null,
    }),
    getEventsByQuery: build.query<EventDTO[] | null, { query?: Partial<EventDTO> }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ query }) => ({
        url: '/events/',
        params: query,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventDTO[]>): (EventDTO[] | null) => response.data || null,
    }),
    getAllEventsBasic: build.query<BasicEventDTO[] | null, { query?: BasicEventsQueryParams }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ query } = {}) => ({
        url: '/events/all/basic',
        params: query,
        method: 'GET',
      }),
      transformResponse: (response: Response<BasicEventDTO[]>): (BasicEventDTO[] | null) => response.data || null,
    }),
    getBusinessEventsBasic: build.query<BasicEventDTO[] | null, {
      businessId: string; active?: boolean; allowInPersonSales?: boolean; notEnded?: boolean;
    }>({
      providesTags: () => [{ type: 'events' }],
      query: ({
        businessId, active, allowInPersonSales, notEnded,
      }) => ({
        url: `/businesses/${businessId}/events/all/basic`,
        method: 'GET',
        params: {
          ...(active !== undefined ? { active } : {}),
          ...(allowInPersonSales !== undefined ? { allow_in_person_sales: allowInPersonSales } : {}),
          ...(notEnded ? { not_ended: true } : {}),
        },
      }),
      transformResponse: (response: Response<BasicEventDTO[]>): (BasicEventDTO[] | null) => response.data || null,
    }),
    getAllBusinessEvents: build.query<Response<EventDTO[]>, number>({
      providesTags: () => [{ type: 'events' }],
      query: (business_id) => ({
        url: `/businesses/${business_id}/events`,
        method: 'GET',
      }),
    }),
    getBusinessEvents: build.query<Response<EventDTO[]>, { businessId: string, onlyActive?: boolean }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ businessId, onlyActive }) => ({
        url: `/businesses/${businessId}/events`,
        method: 'GET',
        params: {
          active: onlyActive,
        },
      }),
    }),
    getCurrentAgent: build.query<AgentDTO | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/agents`,
        method: 'GET',
        params: {
          now: 'true',
        },
      }),
      transformResponse: (response: Response<AgentDTO>): (AgentDTO | null) => response.data || null,
    }),
    createEvent: build.mutation<Response<EventDTO>, { event: Partial<EventDTO>, businessId: string }>({
      query: ({
        event, businessId,
      }) => ({
        url: `/businesses/${businessId}/events/`,
        method: 'POST',
        body: event,
      }),
      // Bust public taxonomy too: a new event in a new city/province changes
      // the event_count and may surface a hub that previously had zero events.
      invalidatesTags: ['events', 'public_taxonomy'],
    }),
    deleteEvent: build.mutation<void, { businessId: string; eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'events', id: eventId }, 'events', 'public_taxonomy'],
    }),
    updateEvent: build.mutation<Response<EventDTO>, { event: Partial<EventDTO>, businessId: string }>({
      query: ({
        event, businessId,
      }) => ({
        url: `/businesses/${businessId}/events/`,
        method: 'PUT',
        body: event,
      }),
      invalidatesTags: ['events', 'public_taxonomy'],
    }),
    getEventQuestions: build.query<QuestionDTO[] | null, { eventId: string }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ eventId }) => ({
        url: `/events/${eventId}/polls/questions`,
        method: 'GET',
      }),
      transformResponse: (response: Response<QuestionDTO[]>): (QuestionDTO[] | null) => response.data || null,
    }),
    getSeatsInfo: build.query<DisplaySeatDTO[] | null, { eventId: string, businessId: string }>({
      providesTags: () => [{ type: 'event_seats' }],
      query: ({ eventId, businessId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/seats?display=true`,
        method: 'GET',
      }),
      transformResponse: (response: Response<DisplaySeatDTO[]>): (DisplaySeatDTO[] | null) => response.data || null,
    }),
    // Light poll of the buyer map: only id + status of every place.
    getSeatsAvailability: build.query<SeatAvailabilityDTO[], { eventId: string, businessId: string }>({
      // Best-effort poll every 12 s on the buyer map: a failed tick changes
      // nothing and must not toast on top of the checkout.
      extraOptions: { silent: true },
      query: ({ eventId, businessId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/seats/availability`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SeatAvailabilityDTO[]>): SeatAvailabilityDTO[] => response.data || [],
    }),
    // "Elegir la mejor": closest to the stage, then to the central aisle.
    getBestSeats: build.query<DisplaySeatDTO[], { eventId: string, businessId: string, sectorId: string, limit?: number }>({
      query: ({
        eventId, businessId, sectorId, limit,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/seats/best`,
        method: 'GET',
        params: { sector_id: sectorId, ...(limit ? { limit } : {}) },
      }),
      transformResponse: (response: Response<DisplaySeatDTO[]>): DisplaySeatDTO[] => response.data || [],
    }),
    blockEventSeats: build.mutation<{ updated: number } | null, { eventId: string; eventSeatIds: string[]; blockReason: EventSeatBlockReason | null; blockNote?: string | null }>({
      query: ({
        eventId, eventSeatIds, blockReason, blockNote,
      }) => ({
        url: `/events/${eventId}/seats/actions/block`,
        method: 'POST',
        body: { event_seat_ids: eventSeatIds, block_reason: blockReason, block_note: blockNote ?? null },
      }),
      invalidatesTags: ['event_seats_raw', 'event_seats'],
      transformResponse: (response: Response<{ updated: number }>): { updated: number } | null => response.data || null,
    }),
    // Public endpoint: returns non-numerated sectors with their tickets and
    // remaining stock. Used to render clickable sector polygons on the
    // venue map alongside numerated seats.
    getEventSectorsForSale: build.query<SectorForSale[], { eventId: string }>({
      providesTags: () => [{ type: 'event_seats' }],
      query: ({ eventId }) => ({
        url: `/events/${eventId}/sectors`,
        method: 'GET',
        params: { for_sale: 1 },
      }),
      transformResponse: (response: Response<SectorForSale[]>): SectorForSale[] => response.data || [],
    }),
    // Raw event-seats (admin authoring) — returns the full event_seat rows so the
    // map editor can reconcile selected venue seats against existing assignments.
    getEventSeatsRaw: build.query<EventSeatDTO[], { eventId: string }>({
      providesTags: (result, error, { eventId }) => [{ type: 'event_seats_raw', id: eventId }, 'event_seats_raw'],
      query: ({ eventId }) => ({
        url: `/events/${eventId}/seats`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventSeatDTO[]>): EventSeatDTO[] => response.data || [],
    }),
    bulkCreateEventSeatsFromIds: build.mutation<EventSeatDTO[], { eventId: string; sectorId: string; venueSeatIds: string[] }>({
      query: ({ eventId, sectorId, venueSeatIds }) => ({
        url: `/events/${eventId}/sectors/${sectorId}/seats/multiple-from-ids`,
        method: 'POST',
        body: { venue_seat_ids: venueSeatIds },
      }),
      invalidatesTags: ['event_seats_raw', 'event_seats'],
      transformResponse: (response: Response<EventSeatDTO[]>): EventSeatDTO[] => response.data || [],
    }),
    updateEventSeat: build.mutation<EventSeatDTO | null, { eventId: string; sectorId: string; eventSeatId: string; body: { event_sector_id?: string; status?: string; name?: string } }>({
      query: ({
        eventId, sectorId, eventSeatId, body,
      }) => ({
        url: `/events/${eventId}/sectors/${sectorId}/seats/${eventSeatId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['event_seats_raw', 'event_seats'],
      transformResponse: (response: Response<EventSeatDTO>): EventSeatDTO | null => response.data || null,
    }),
    deleteEventSeat: build.mutation<void, { eventId: string; sectorId: string; eventSeatId: string }>({
      query: ({ eventId, sectorId, eventSeatId }) => ({
        url: `/events/${eventId}/sectors/${sectorId}/seats/${eventSeatId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['event_seats_raw', 'event_seats'],
    }),
    getEventUsers: build.query<UserDTO[] | null, { eventId: string, businessId: string }>({
      providesTags: () => [{ type: 'events_users' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users`,
        method: 'GET',
      }),
      transformResponse: (response: Response<UserDTO[]>): (UserDTO[] | null) => response.data || null,
    }),
    addUserToEvent: build.mutation<Response<UserEvent>, { businessId: string, eventId: string, userData: { userId: string, comissionRate: number, active: boolean, permissions: string, seller_group_id?: string, staff_door_id?: string | null } }>({
      query: ({
        businessId, eventId, userData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users`,
        method: 'POST',
        body: {
          user_id: userData.userId,
          seller_comission_rate: userData.comissionRate,
          permissions: userData.permissions,
          active: userData.active,
          seller_group_id: userData.seller_group_id,
          staff_door_id: userData.staff_door_id,
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ordersApi.util.invalidateTags(['seller_summary']));
          dispatch(businessesApi.util.invalidateTags(['businesses']));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ['events_users', 'seller_summary', 'orders', 'businesses'],
    }),
    updateUserEvent: build.mutation<Response<UserEvent>, { businessId: string, eventId: string, data: Partial<UserEvent> & { id: string } }>({
      query: ({
        businessId, eventId, data,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users`,
        method: 'PUT',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ordersApi.util.invalidateTags(['seller_summary']));
          dispatch(businessesApi.util.invalidateTags(['businesses']));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ['events_users', 'seller_summary'],
    }),
    deleteUserEvent: build.mutation<Response<UserEvent>, { businessId: string, eventId: string, userEventId: string }>({
      query: ({ businessId, eventId, userEventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users/${userEventId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(referralsApi.util.invalidateTags(['referrals']));
          dispatch(businessesApi.util.invalidateTags(['businesses']));
          dispatch(ordersApi.util.invalidateTags(['seller_summary']));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ['events_users', 'businesses', 'seller_summary'],
    }),
    addEventTransaction: build.mutation<Response<CreateEventTransactionDTO>, { businessId: string, eventId: string, transactionData: CreateEventTransactionDTO }>({
      query: ({
        businessId, eventId, transactionData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/transactions`,
        method: 'POST',
        body: transactionData,
      }),
      invalidatesTags: ['events_transactions'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ordersApi.util.invalidateTags(['seller_summary']));
        } catch (e) {
          console.error(e);
        }
      },
    }),
    updateEventTransaction: build.mutation<Response<EventTransactionDTO>, { businessId: string, eventId: string, transactionId: string, transactionData: Partial<EventTransactionDTO> }>({
      query: ({
        businessId, eventId, transactionId, transactionData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/transactions/${transactionId}`,
        method: 'PUT',
        body: transactionData,
      }),
      invalidatesTags: ['events_transactions'],
    }),
    deleteEventTransaction: build.mutation<Response<EventTransactionDTO>, { businessId: string, eventId: string, transactionId: string }>({
      query: ({ businessId, eventId, transactionId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/transactions/${transactionId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ordersApi.util.invalidateTags(['seller_summary']));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ['events_transactions'],
    }),
    getEventTransactions: build.query<Response<EventTransactionDTO[]>, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'events_transactions' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/transactions`,
        method: 'GET',
      }),
    }),
    getEventCapacity: build.query<{ used_tickets: number } | null, { eventId: string, businessId: string }>({
      // Dedicated tag so broad `invalidatesTags: ['events']` / event delete refetches do not hit
      // /capacity while the event is gone (would show backend error toast on EventMenu unload).
      providesTags: (result, error, { eventId }) => [{ type: 'event_capacity', id: eventId }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/capacity`,
        method: 'GET',
      }),
      transformResponse: (response: Response<{ used_tickets: number }>): ({ used_tickets: number } | null) => response.data || null,
    }),
    countSellerQrCode: build.mutation<Response<null>, { businessId: string, eventId: string, userEventId: string }>({
      query: ({ businessId, eventId, userEventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users/${userEventId}/qr/count`,
        method: 'POST',
      }),
    }),
    getSellerEventQr: build.query<SellerEventQR | null, { businessId: string, eventId: string, userEventId: string }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ businessId, eventId, userEventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/users/${userEventId}/qr`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SellerEventQR>): (SellerEventQR | null) => response.data || null,
    }),
    getEventBalance: build.query<EventBalanceDTO | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/balance`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventBalanceDTO>): (EventBalanceDTO | null) => response.data || null,
    }),
    createWireTransfer: build.mutation<Response<WireTransferDTO>, { wire: CreateWireTransferDTO, businessId: string, eventId: string }>({
      query: ({ wire, businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/wires`,
        method: 'POST',
        body: wire,
      }),
      invalidatesTags: ['events'],
    }),
    importSellersToEvent: build.mutation<Response<null>, { businessId: string, eventId: string, eventIdToImport: string }>({
      query: ({ businessId, eventId, eventIdToImport }) => ({
        url: `/businesses/${businessId}/events/${eventId}/import-users`,
        method: 'POST',
        body: {
          import_from: eventIdToImport,
        },
      }),
      invalidatesTags: ['events_users', 'seller_summary'],
    }),
    reserveSeat: build.mutation<Response<EventSeatDTO>, { reservationCode: string, businessId: string, eventId: string, eventSeatId: string }>({
      query: ({
        reservationCode, businessId, eventId, eventSeatId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/seats/${eventSeatId}/reserve`,
        method: 'POST',
        body: { code: reservationCode },
      }),
      // No refetch of the whole map on every tap: the buyer map paints its own
      // selection right away and the availability poll brings everyone else's.
    }),
    freeSeat: build.mutation<Response<EventSeatDTO>, { reservationCode: string, businessId: string, eventId: string, eventSeatId: string }>({
      query: ({
        reservationCode, businessId, eventId, eventSeatId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/seats/${eventSeatId}/free`,
        method: 'POST',
        body: { code: reservationCode },
      }),
      // No refetch of the whole map on every tap: the buyer map paints its own
      // selection right away and the availability poll brings everyone else's.
    }),
    getEventAssistanceReport: build.query<EventAssistanceReportDTO | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'event_assistance_report' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/report/assistance`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventAssistanceReportDTO>): (EventAssistanceReportDTO | null) => response.data || null,
    }),
    getEventSellersReport: build.query<SellerReferralsReportDTO | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'event_sellers_report' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/report/referrals-sellers`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SellerReferralsReportDTO>): (SellerReferralsReportDTO | null) => response.data || null,
    }),
    createAudienceDiscount: build.mutation<Response<EventAudienceDiscountDTO>, { businessId: string, eventId: string, discountData: CreateEventAudienceDiscountDTO }>({
      invalidatesTags: ['event_audience_discounts'],
      query: ({ businessId, eventId, discountData }) => ({
        url: `/businesses/${businessId}/events/${eventId}/audience-discounts`,
        method: 'POST',
        body: discountData,
      }),
    }),
    getAudienceDiscounts: build.query<Response<EventAudienceDiscountDTO[]>, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'event_audience_discounts' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/audience-discounts`,
        method: 'GET',
      }),
    }),
    updateAudienceDiscount: build.mutation<Response<EventAudienceDiscountDTO>, { businessId: string, eventId: string, discountId: string, discountData: Partial<EventAudienceDiscountDTO> }>({
      invalidatesTags: ['event_audience_discounts'],
      query: ({
        businessId, eventId, discountId, discountData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/audience-discounts`,
        method: 'PUT',
        body: {
          ...discountData,
          id: discountId,
        },
      }),
    }),
    deleteAudienceDiscount: build.mutation<Response<EventAudienceDiscountDTO>, { businessId: string, eventId: string, discountId: string }>({
      invalidatesTags: ['event_audience_discounts'],
      query: ({ businessId, eventId, discountId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/audience-discounts/${discountId}`,
        method: 'DELETE',
      }),
    }),
    createShowtime: build.mutation<Response<ShowtimeDTO>, { businessId: string, eventId: string, showtimeData: CreateShowtimeDTO }>({
      query: ({ businessId, eventId, showtimeData }) => ({
        url: `/businesses/${businessId}/events/${eventId}/showtimes`,
        method: 'POST',
        body: showtimeData,
      }),
      invalidatesTags: ['showtimes'],
    }),
    updateShowtime: build.mutation<Response<ShowtimeDTO>, { businessId: string, eventId: string, showtimeId: string, showtimeData: Partial<ShowtimeDTO> }>({
      query: ({
        businessId, eventId, showtimeId, showtimeData,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/showtimes/${showtimeId}`,
        method: 'PUT',
        body: showtimeData,
      }),
      invalidatesTags: ['showtimes'],
    }),
    deleteShowtime: build.mutation<Response<ShowtimeDTO>, { businessId: string, eventId: string, showtimeId: string }>({
      query: ({ businessId, eventId, showtimeId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/showtimes/${showtimeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['showtimes'],
    }),
    getShowtimes: build.query<ShowtimeDTO[] | null, { businessId: string, eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/showtimes`,
        method: 'GET',
      }),
      providesTags: ['showtimes'],
      transformResponse: (response: Response<ShowtimeDTO[]>): (ShowtimeDTO[] | null) => response.data || null,
    }),
    getEventSubscription: build.query<EventSubscriptionDTO[] | null, { businessId: string, eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/subscriptions`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventSubscriptionDTO[]>): (EventSubscriptionDTO[] | null) => response.data || null,
    }),
    createEventSubscription: build.mutation<Response<EventSubscriptionDTO>, { businessId: string, eventId: string, subscriptionData: CreateEventSubscriptionDTO }>({
      query: ({ businessId, eventId, subscriptionData }) => ({
        url: `/businesses/${businessId}/events/${eventId}/subscriptions`,
        method: 'POST',
        body: subscriptionData,
      }),
      invalidatesTags: ['events'],
    }),
    deleteEventBySuscribedId: build.mutation<Response<EventSubscriptionDTO>, { businessId: string, eventId: string, suscribedId: string }>({
      query: ({ businessId, eventId, suscribedId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/subscriptions/by-event/${suscribedId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['events'],
    }),
    cloneEvent: build.mutation<Response<{ event: EventDTO }>, { businessId: string, eventId: string, name: string }>({
      query: ({ businessId, eventId, name }) => ({
        url: `/businesses/${businessId}/events/${eventId}/clone`,
        method: 'POST',
        body: {
          name,
        },
      }),
      invalidatesTags: ['events'],
    }),
    getCategories: build.query<Response<CategoryDTO[]>, void>({
      query: () => ({
        url: '/categories',
        method: 'GET',
      }),
    }),
    // Public SEO taxonomy endpoints. See backend Swagger spec under
    // `Public Discovery`. Each endpoint returns the standard `{ msg, data }`
    // envelope; consumers should read `.data` and tolerate empty arrays.
    getPublicProvinces: build.query<Response<ProvinceDTO[]>, void>({
      query: () => ({
        url: '/public/provinces',
        method: 'GET',
      }),
      providesTags: ['public_taxonomy'],
    }),
    getPublicCitiesByProvince: build.query<Response<CityDTO[]>, { provinceSlug: string }>({
      query: ({ provinceSlug }) => ({
        url: `/public/cities/${provinceSlug}`,
        method: 'GET',
      }),
      providesTags: ['public_taxonomy'],
    }),
    getPublicCategories: build.query<Response<PublicCategoryDTO[]>, void>({
      query: () => ({
        url: '/public/categories',
        method: 'GET',
      }),
      transformResponse: (response: Response<PublicCategoryDTO[]>): Response<PublicCategoryDTO[]> => ({
        ...response,
        data: dedupePublicCategoriesBySlug(response.data ?? []),
      }),
      providesTags: ['public_taxonomy'],
    }),
    getPublicProducers: build.query<Response<PublicProducerDTO[]>, void>({
      query: () => ({
        url: '/public/producers',
        method: 'GET',
      }),
      providesTags: ['public_taxonomy'],
    }),
    getPublicCategoryEvents: build.query<Response<EventDTO[]>, { categorySlug: string }>({
      query: ({ categorySlug }) => ({
        url: `/public/categories/${encodeURIComponent(categorySlug)}/events`,
        method: 'GET',
      }),
      providesTags: ['public_taxonomy'],
    }),
    getCategoriesByEventId: build.query<Response<EventCategoryDTO[]>, { eventId: string, businessId: string }>({
      query: ({ eventId, businessId }) => ({
        url: `businesses/${businessId}/events/${eventId}/categories`,
        method: 'GET',
      }),
      providesTags: ['event_categories'],
    }),
    createEventCategory: build.mutation<Response<EventCategoryDTO>, { eventId: string, categoryId: string, businessId: string }>({
      query: ({ eventId, categoryId, businessId }) => ({
        url: `businesses/${businessId}/events/${eventId}/categories`,
        method: 'POST',
        body: { category_id: categoryId },
      }),
      // Public categories carry an `event_count` that changes when an event
      // gets a new category linked to it.
      invalidatesTags: ['event_categories', 'public_taxonomy'],
    }),
    deleteEventCategory: build.mutation<Response<void>, { eventId: string, eventCategoryId: string, businessId: string }>({
      query: ({ eventId, eventCategoryId, businessId }) => ({
        url: `businesses/${businessId}/events/${eventId}/categories/${eventCategoryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['event_categories', 'public_taxonomy'],
    }),
    getBusinessEventPollQuestions: build.query<QuestionDTO[] | null, { eventId: string, businessId: string }>({
      providesTags: (result, error, { eventId }) => [{ type: 'poll_questions', id: eventId }],
      query: ({ eventId, businessId }) => ({
        url: `businesses/${businessId}/events/${eventId}/polls/questions`,
        method: 'GET',
      }),
      transformResponse: (response: Response<QuestionDTO[]>): (QuestionDTO[] | null) => response.data || null,
    }),
    createPollQuestion: build.mutation<Response<QuestionDTO>, { eventId: string, businessId: string, question: CreatePollQuestionDTO }>({
      query: ({ eventId, businessId, question }) => ({
        url: `businesses/${businessId}/events/${eventId}/polls/questions`,
        method: 'POST',
        body: question,
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'poll_questions', id: eventId }],
    }),
    updatePollQuestion: build.mutation<Response<QuestionDTO>, {
      eventId: string,
      businessId: string,
      questionId: string,
      question: UpdatePollQuestionDTO,
    }>({
      query: ({
        eventId, businessId, questionId, question,
      }) => ({
        url: `businesses/${businessId}/events/${eventId}/polls/questions/${questionId}`,
        method: 'PUT',
        body: question,
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'poll_questions', id: eventId }],
    }),
    deletePollQuestion: build.mutation<Response<QuestionDTO>, { eventId: string, businessId: string, questionId: string }>({
      query: ({ eventId, businessId, questionId }) => ({
        url: `businesses/${businessId}/events/${eventId}/polls/questions/${questionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'poll_questions', id: eventId }],
    }),
    getBusinessEventAttendeeFields: build.query<AttendeeFieldDTO[] | null, { eventId: string, businessId: string }>({
      providesTags: (result, error, { eventId }) => [{ type: 'attendee_fields', id: eventId }],
      query: ({ eventId, businessId }) => ({
        url: `businesses/${businessId}/events/${eventId}/attendee-fields`,
        method: 'GET',
      }),
      transformResponse: (response: Response<AttendeeFieldDTO[]>): (AttendeeFieldDTO[] | null) => response.data || null,
    }),
    createAttendeeField: build.mutation<Response<AttendeeFieldDTO>, { eventId: string, businessId: string, attendeeField: CreateAttendeeFieldDTO }>({
      query: ({ eventId, businessId, attendeeField }) => ({
        url: `businesses/${businessId}/events/${eventId}/attendee-fields`,
        method: 'POST',
        body: attendeeField,
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'attendee_fields', id: eventId }],
    }),
    updateAttendeeField: build.mutation<Response<AttendeeFieldDTO>, {
      eventId: string,
      businessId: string,
      attendeeFieldId: string,
      attendeeField: UpdateAttendeeFieldDTO,
    }>({
      query: ({
        eventId, businessId, attendeeFieldId, attendeeField,
      }) => ({
        url: `businesses/${businessId}/events/${eventId}/attendee-fields`,
        method: 'PUT',
        body: { ...attendeeField, id: attendeeFieldId },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'attendee_fields', id: eventId }],
    }),
    deleteAttendeeField: build.mutation<Response<AttendeeFieldDTO>, { eventId: string, businessId: string, attendeeFieldId: string }>({
      query: ({ eventId, businessId, attendeeFieldId }) => ({
        url: `businesses/${businessId}/events/${eventId}/attendee-fields/${attendeeFieldId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'attendee_fields', id: eventId }],
    }),
    getEventResponsesTable: build.query<PollResponse[] | null, { eventId: string, businessId: string }>({
      query: ({ eventId, businessId }) => ({
        url: `businesses/${businessId}/events/${eventId}/polls/responses-table`,
        method: 'GET',
      }),
    }),
    getEventResponsesByOrderId: build.mutation<PollResponse[] | null, { eventId: string, businessId: string, orderId: string }>({
      query: ({ eventId, businessId, orderId }) => ({
        url: `businesses/${businessId}/events/${eventId}/polls/responses-by-order-id/${orderId}`,
        method: 'GET',
      }),
    }),
    getEventsSummary: build.query<EventSummaryDTO[] | null, void>({
      query: () => ({
        url: '/events/all/summary',
        method: 'GET',
      }),
      transformResponse: (response: Response<EventSummaryDTO[]>): (EventSummaryDTO[] | null) => response.data || null,
    }),
    getAllEventsPendingBalance: build.query<AllEventsPendingBalanceDTO | null, void>({
      providesTags: () => [{ type: 'events' }],
      query: () => ({
        url: '/events/all/pending-balance',
        method: 'GET',
      }),
      transformResponse: (response: Response<AllEventsPendingBalanceDTO>): (AllEventsPendingBalanceDTO | null) => response.data || null,
    }),
    getPendingBalanceAt: build.query<PendingBalanceAtDTO | null, { at: string }>({
      providesTags: () => [{ type: 'events' }],
      query: ({ at }) => ({
        url: '/events/all/pending-balance-at',
        method: 'GET',
        params: { at },
      }),
      transformResponse: (response: Response<PendingBalanceAtDTO>): (PendingBalanceAtDTO | null) => response.data || null,
    }),
    getPlatformSalesChart: build.query<PlatformSalesChartDTO | null, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/events/all/sales-chart',
        method: 'GET',
        params: {
          start: startDate,
          end: endDate,
        },
      }),
      transformResponse: (response: Response<PlatformSalesChartDTO>): (PlatformSalesChartDTO | null) => response.data || null,
    }),
    getPlatformCharts: build.query<PlatformChartsDTO | null, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/events/all/platform-charts',
        method: 'GET',
        params: {
          start: startDate,
          end: endDate,
        },
      }),
      transformResponse: (response: Response<PlatformChartsDTO>): (PlatformChartsDTO | null) => response.data || null,
    }),
    getPlatformRangeCharts: build.query<PlatformRangeChartsDTO | null, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => {
        const bounds = toArgentinaReportBounds(startDate, endDate);
        return {
          url: '/events/all/platform-range-charts',
          method: 'GET',
          params: {
            start: bounds.start,
            end: bounds.end,
          },
        };
      },
      transformResponse: (response: Response<PlatformRangeChartsDTO>): (PlatformRangeChartsDTO | null) => response.data || null,
    }),
    getPlatformMonthlyCharts: build.query<PlatformMonthlyChartsDTO | null, void>({
      query: () => ({
        url: '/events/all/platform-monthly-charts',
        method: 'GET',
      }),
      transformResponse: (response: Response<PlatformMonthlyChartsDTO>): (PlatformMonthlyChartsDTO | null) => response.data || null,
    }),
    getTopGrossing: build.query<TopGrossingDTO | null, { startDate: string; endDate: string; limit?: number }>({
      query: ({ startDate, endDate, limit }) => {
        const bounds = toArgentinaReportBounds(startDate, endDate);
        return {
          url: '/events/all/top-grossing',
          method: 'GET',
          params: {
            start: bounds.start,
            end: bounds.end,
            ...(limit != null && { limit }),
          },
        };
      },
      transformResponse: (response: Response<TopGrossingDTO>): (TopGrossingDTO | null) => response.data || null,
    }),
    getEventsSummaryXlsx: build.mutation<void, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate } = {}) => ({
        url: '/events/all/summary/xlsx',
        method: 'GET',
        params: {
          ...(startDate && { start: startDate }),
          ...(endDate && { end: endDate }),
        },
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const dateSuffix = startDate && endDate
            ? `${startDate}_${endDate}`
            : new Date().toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' });
          a.download = `summary-events-${dateSuffix}.xlsx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
      }),
    }),
    getEventStats: build.query<EventStatsDTO | null, string | undefined>({
      providesTags: () => [{ type: 'events' }],
      query: (user_id) => ({
        url: `/events/all/stats/${user_id || ''}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventStatsDTO>): (EventStatsDTO | null) => response.data || null,
    }),
    getEventSummaryPDF: build.mutation<void, { businessId: string, eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/summary-pdf`,
        method: 'GET',
        responseHandler: async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}. ${errorText}`);
          }
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorText = await response.text();
            throw new Error(`Server returned JSON instead of PDF: ${errorText.substring(0, 200)}`);
          }
          const blob = await response.blob();
          if (blob.size === 0) {
            throw new Error('Received empty PDF file');
          }
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `summary-events-${new Date().toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' })}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
      }),
    }),
    getEventItemsExport: build.query<ItemExportRow[] | null, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/events/${eventId}/items/export`,
        method: 'GET',
      }),
      transformResponse: (response: Response<ItemExportRow[]>): (ItemExportRow[] | null) => response.data || null,
    }),
  }),
});

export const {
  useGetEventByIdQuery,
  useLazyGetEventByIdQuery,
  useGetAllBusinessEventsQuery,
  useGetBusinessEventsQuery,
  useGetCurrentAgentQuery,
  useGetAllEventsQuery,
  useCreateEventMutation,
  useDeleteEventMutation,
  useUpdateEventMutation,
  useGetEventQuestionsQuery,
  useGetSeatsInfoQuery,
  useGetSeatsAvailabilityQuery,
  useLazyGetBestSeatsQuery,
  useBlockEventSeatsMutation,
  useGetEventSectorsForSaleQuery,
  useGetEventSeatsRawQuery,
  useBulkCreateEventSeatsFromIdsMutation,
  useUpdateEventSeatMutation,
  useDeleteEventSeatMutation,
  useGetEventUsersQuery,
  useAddUserToEventMutation,
  useDeleteUserEventMutation,
  useAddEventTransactionMutation,
  useDeleteEventTransactionMutation,
  useUpdateEventTransactionMutation,
  useGetEventTransactionsQuery,
  useGetEventCapacityQuery,
  useCountSellerQrCodeMutation,
  useGetSellerEventQrQuery,
  useLazyGetSellerEventQrQuery,
  useUpdateUserEventMutation,
  useGetEventBalanceQuery,
  useLazyGetEventBalanceQuery,
  useCreateWireTransferMutation,
  useImportSellersToEventMutation,
  useReserveSeatMutation,
  useFreeSeatMutation,
  useGetEventAssistanceReportQuery,
  useLazyGetEventAssistanceReportQuery,
  useGetEventSellersReportQuery,
  useGetEventsByQueryQuery,
  useGetAllEventsBasicQuery,
  useGetBusinessEventsBasicQuery,
  useCreateAudienceDiscountMutation,
  useGetAudienceDiscountsQuery,
  useUpdateAudienceDiscountMutation,
  useDeleteAudienceDiscountMutation,
  useCreateShowtimeMutation,
  useUpdateShowtimeMutation,
  useDeleteShowtimeMutation,
  useGetShowtimesQuery,
  useGetEventSubscriptionQuery,
  useCreateEventSubscriptionMutation,
  useDeleteEventBySuscribedIdMutation,
  useGetCategoriesByEventIdQuery,
  useGetCategoriesQuery,
  useCreateEventCategoryMutation,
  useDeleteEventCategoryMutation,
  useGetPublicProvincesQuery,
  useGetPublicCitiesByProvinceQuery,
  useGetPublicCategoriesQuery,
  useGetPublicProducersQuery,
  useGetPublicCategoryEventsQuery,
  useGetEventBySlugQuery,
  useCloneEventMutation,
  useGetEventResponsesByOrderIdMutation,
  useGetEventResponsesTableQuery,
  useGetBusinessEventPollQuestionsQuery,
  useCreatePollQuestionMutation,
  useUpdatePollQuestionMutation,
  useDeletePollQuestionMutation,
  useGetBusinessEventAttendeeFieldsQuery,
  useCreateAttendeeFieldMutation,
  useUpdateAttendeeFieldMutation,
  useDeleteAttendeeFieldMutation,
  useGetEventsSummaryQuery,
  useGetAllEventsPendingBalanceQuery,
  useGetPendingBalanceAtQuery,
  useGetEventsSummaryXlsxMutation,
  useGetPlatformSalesChartQuery,
  useGetPlatformChartsQuery,
  useGetPlatformRangeChartsQuery,
  useGetPlatformMonthlyChartsQuery,
  useGetTopGrossingQuery,
  useGetEventStatsQuery,
  useGetEventSummaryPDFMutation,
  useGetEventItemsExportQuery,
} = eventsApi;
export { eventsApi };
